import { untrack } from 'svelte';
import { SvelteMap } from 'svelte/reactivity';

export type VirtualItem = {
  index: number;
  start: number;
  end: number;
  size: number;
  key: number;
};

export type VirtualizerOptions = {
  scrollElement: () => Element | undefined;
  count: () => number;
  estimatedSize: number;
  overscan: number;
};

export function createVirtualizer(options: VirtualizerOptions) {
  let scrollTop = $state(0);
  let viewportHeight = $state(0);

  // Measured row heights, keyed by row index
  const measuredHeights = new SvelteMap<number, number>();

  // Track which elements we're observing
  const observedElements = new SvelteMap<Element, number>();
  let resizeObserver: ResizeObserver;

  // Hook scroll events on the scroll element
  $effect(() => {
    const elem = options.scrollElement();

    if (!elem) return;

    const onScroll = () => {
      const elem = options.scrollElement();

      if (!elem) return;

      scrollTop = elem.scrollTop;
      viewportHeight = elem instanceof HTMLElement ? elem.clientHeight : window.innerHeight;
    };

    elem.addEventListener('scroll', onScroll, { passive: true });

    // Initialize
    scrollTop = elem.scrollTop;
    viewportHeight = elem instanceof HTMLElement ? elem.clientHeight : window.innerHeight;

    return () => {
      elem?.removeEventListener('scroll', onScroll);
    };
  });

  // Initialization and cleanup logic
  $effect(() => {
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const index = observedElements.get(entry.target);

        if (index === undefined) continue;

        const height = Math.ceil(
          entry.borderBoxSize?.[0]?.blockSize ?? entry.target.getBoundingClientRect().height,
        );

        if (height > 0) measuredHeights.set(index, height);
      }
    });

    return () => {
      resizeObserver.disconnect();
      observedElements.clear();
    };
  });

  // Clear measurements when row count changes significantly
  $effect(() => {
    options.count();

    untrack(() => {
      observedElements.clear();
      measuredHeights.clear();
    });
  });

  function getRowHeight(index: number): number {
    return measuredHeights.get(index) ?? options.estimatedSize;
  }

  // Compute total height and row offsets
  const totalSize = $derived.by(() => {
    const count = options.count();
    let total = 0;
    for (let i = 0; i < count; i++) {
      total += getRowHeight(i);
    }
    return total;
  });

  const virtualItems = $derived.by((): VirtualItem[] => {
    const count = options.count();
    if (count === 0) return [];

    const top = scrollTop;
    const bottom = top + viewportHeight;
    const overscan = options.overscan;

    // Find start index
    let offset = 0;
    let startIdx = 0;
    for (let i = 0; i < count; i++) {
      const h = getRowHeight(i);
      if (offset + h > top) {
        startIdx = i;
        break;
      }
      offset += h;
      startIdx = i;
    }

    // Apply overscan before
    startIdx = Math.max(0, startIdx - overscan);

    // Recalculate offset for overscan-adjusted start
    offset = 0;
    for (let i = 0; i < startIdx; i++) {
      offset += getRowHeight(i);
    }

    // Collect visible items + overscan after
    const items: VirtualItem[] = [];
    let currentOffset = offset;
    let passedBottom = false;
    let overscanRemaining = overscan;

    for (let i = startIdx; i < count; i++) {
      const h = getRowHeight(i);
      items.push({
        index: i,
        start: currentOffset,
        end: currentOffset + h,
        size: h,
        key: i,
      });
      currentOffset += h;

      if (currentOffset >= bottom && !passedBottom) {
        passedBottom = true;
      }

      if (passedBottom) {
        overscanRemaining -= 1;

        if (overscanRemaining <= 0) break;
      }
    }

    return items;
  });

  function measureElement(element: HTMLTableRowElement | undefined, index: number) {
    if (!element) {
      // Unmounted - remove from observer
      for (const [el, idx] of observedElements) {
        if (idx !== index) continue;

        resizeObserver?.unobserve(el);
        observedElements.delete(el);
        break;
      }

      return;
    }

    // Already observing this element for this index
    if (observedElements.get(element) === index) return;

    // Clean up any previous observation of this index
    for (const [el, idx] of observedElements) {
      if (idx === index && el !== element) {
        resizeObserver?.unobserve(el);
        observedElements.delete(el);
        break;
      }
    }

    observedElements.set(element, index);
    resizeObserver?.observe(element);

    // Immediately measure (ceil avoids subpixel gaps between absolute virtual rows).
    const height = Math.ceil(element.getBoundingClientRect().height);
    if (height > 0) measuredHeights.set(index, height);
  }

  return {
    get totalSize() {
      return totalSize;
    },
    get virtualItems() {
      return virtualItems;
    },
    get scrollTop() {
      return scrollTop;
    },
    setScrollOffset(offset: number) {
      scrollTop = offset;
    },
    measureElement,
  };
}

export type Virtualizer = ReturnType<typeof createVirtualizer>;
